import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CompareHelper } from '@barthauer/abc/core';
import { RouteConfiguration, RoutingService } from '@barthauer/basys/map-frame';
import { Observable, Subject } from 'rxjs';
import { ComponentDataSource } from '../models/componentDataSource.model';
import { ResourcesDataContext } from '../resources/resources.model';
import { ResourcesService } from '../resources/resources.service';
import { OkDialogComponent } from '../shared/ok-dialog.component';
import { AppService } from '../services/app.service';
import { FrameService } from '../services/frame.service';
import { IMobileWork } from '../viewModels/mobileData.viewModel';
import { WorkNavigationContext, WorksDataContext, WorkStateChangeArg } from './works.model';

@Injectable()
export class WorksService {
  public isResourceListVisible: boolean;
  private dataContextSubject = new Subject<WorksDataContext>();
  private refreshNavigationContextSubject = new Subject<WorkNavigationContext>();
  private workStateChangedSubject = new Subject<WorkStateChangeArg>();
  private context: WorkNavigationContext;

  constructor(
    private routingService: RoutingService,
    private frame: FrameService,
    private resourcesService: ResourcesService,
    private dialog: MatDialog,
  ) {}

  private hasOpenOkDialog(): boolean {
    return this.dialog.openDialogs.some((dialogRef) => dialogRef.componentInstance instanceof OkDialogComponent);
  }

  raiseWorkStateChanged(data: WorkStateChangeArg) {
    this.workStateChangedSubject.next(data);
  }

  onWorkStateChanged(): Observable<WorkStateChangeArg> {
    return this.workStateChangedSubject.asObservable();
  }

  sendDataContext(dataContext: WorksDataContext) {
    this.dataContextSubject.next(dataContext);
  }

  onDataContextSent(): Observable<WorksDataContext> {
    return this.dataContextSubject.asObservable();
  }

  refreshNavigationContext(context: WorkNavigationContext) {
    this.context = context;
    this.refreshNavigationContextSubject.next(this.context);
  }

  onNavigationContextRefresh(): Observable<WorkNavigationContext> {
    return this.refreshNavigationContextSubject.asObservable();
  }

  navigateTo(target: string, taskIndex?: number, autoOpen: boolean = false) {
    const current = this.routingService.getCurrentConfig();
    const allowNavigateToWorkDetailFromTaskDetail =
      current?.designation === AppService.Route_TaskDetail && target === AppService.Route_WorkDetail;

    if (this.hasOpenOkDialog() && !allowNavigateToWorkDetailFromTaskDetail) {
      return;
    }

    if (target !== current.designation) {
      let navigationAllowed: boolean = true;
      if (this.frame.bottomSheetToggleCondition) {
        navigationAllowed = this.frame.bottomSheetToggleCondition(target);
      }

      if (navigationAllowed) {
        this.frame.ensurePersistingState(() =>
          this.internalNavigateTo(this.routingService.getCurrentConfig(), taskIndex, target, autoOpen),
        );
      }
    }
  }

  navigateContext(increment: number) {
    let navigationAllowed: boolean = true;
    const current = this.routingService.getCurrentConfig();
    if (this.frame.bottomSheetToggleCondition) {
      navigationAllowed = this.frame.bottomSheetToggleCondition(current?.designation);
    }
    if (navigationAllowed) {
      this.frame.ensurePersistingState(() => this.internalNavigateContext(increment));
    }
  }

  private internalNavigateTo(
    current: RouteConfiguration,
    taskIndex: number,
    target: string,
    autoOpen: boolean = false,
  ) {
    this.context.sourceRoute = current;
    if (taskIndex !== undefined) {
      this.context.taskIndex = taskIndex;
    }

    this.context.autoOpen = autoOpen; // 🔥 Set flag to trigger auto-expansion later

    this.resourcesService.ensureDiscardResourceEventWhenNavigateBack();
    this.resourcesService.refreshNavigationContext(null);

    if (target === AppService.Route_WorkDetail_ResourceList) {
      const that: WorksService = this; //unbelievable :(
      this.routingService.navigateName(AppService.Route_WorkDetail_ResourceList).then(() => {
        const resourcesDataContext: ResourcesDataContext = new ResourcesDataContext();
        resourcesDataContext.dsResourceTypes = this.frame.resourceTypeViewPackage.allResourceTypes;
        resourcesDataContext.workId = that.context.work?.id;
        this.resourcesService.sendDataContext(resourcesDataContext);
      });
    } else if (target === AppService.Route_WorkDetail || target === AppService.Route_TaskDetail) {
      this.isResourceListVisible = false;
      this.routingService.navigateName(target).then((succ) => {
        if (succ) {
          this.refreshNavigationContext(this.context);
        }
      });
    } else {
      this.isResourceListVisible = false;
      this.routingService.navigateName(target);
    }
  }

  private internalNavigateContext(increment: number) {
    const conf = this.routingService.getCurrentConfig();
    document.getElementById('scrollArea').scrollTop = 0;
    if (conf) {
      switch (conf.designation) {
        case AppService.Route_WorkDetail: {
          let newIndex = this.context.workIndex + increment;
          const dataSource = this.context.workDataContext.dataSource;
          if (newIndex >= 0 && newIndex <= dataSource.filteredData.length - 1) {
            if (CompareHelper.isEmptyObject(dataSource.data[newIndex])) {
              const nextIndex = this.tryGetNextWorkIndex(dataSource, newIndex, increment);
              newIndex = nextIndex > -1 ? nextIndex : newIndex;
            }
            this.context.workIndex = newIndex;
            this.context.taskIndex = undefined;
          }
          break;
        }
        case AppService.Route_TaskDetail: {
          this.moveToNextVisibleTask(increment);
          break;
        }
      }
    }
    this.refreshNavigationContext(this.context);
  }

  private moveToNextVisibleTask(increment: number) {
    const newIndex = this.context.taskIndex + increment;
    if (newIndex >= 0 && newIndex <= this.context.work.tasks.length - 1) {
      if (this.context.work.tasks[newIndex].isVisible) {
        this.context.taskIndex = newIndex;
      } else {
        const step = increment > 0 ? 1 : increment < 0 ? -1 : 0;
        if (step !== 0) {
          this.moveToNextVisibleTask(increment + step);
        }
      }
    }
  }

  private tryGetNextWorkIndex(dataSource: ComponentDataSource<IMobileWork>, index: number, increment: number): number {
    if (dataSource?.filteredData.length > index && index >= 0) {
      if (CompareHelper.isEmptyObject(dataSource.data[index])) {
        if (increment === 1) {
          index++;
        } else {
          index--;
        }
        return this.tryGetNextWorkIndex(dataSource, index, increment);
      } else {
        return index;
      }
    } else {
      return -1;
    }
  }

  public toggleResourceListView(): void {
    if (!this.isResourceListVisible) {
      this.isResourceListVisible = true;
      this.navigateTo(AppService.Route_WorkDetail_ResourceList);
    } else {
      this.isResourceListVisible = false;
      this.navigateTo(AppService.Route_WorkDetail);
    }
  }

  public getCurrentRessourceEventDetailName(): string {
    return this.resourcesService.context?.resourceEventGroup?.resourceEventTypeGroup?.resourceEventTypeKindName;
  }

  clearNavigationContext() {
    if (this.context) {
      this.context.lastExpandedElementId = null;
      this.context.autoOpen = false;
      this.context.taskIndex = null;
      this.context.workId = null;
      this.context.workIndex = null;
    }
    this.refreshNavigationContext(this.context);
  }
}
